import {useCallback} from 'react';
import {useQuery} from '@apollo/client';
import {
    GET_CUSTOMER_COMPLAINT_COUNT,
    GET_CUSTOMER_COMPLAINT_COUNT_FOR_HQ,
    GET_CUSTOMER_COMPLAINT_COUNT_FOR_WMS,
    GetCustomerComplaintCountForHqResponse,
    GetCustomerComplaintCountForWmsResponse,
    GetCustomerComplaintCountResponse,
    GetCustomerComplaintCountVariables,
} from '../graphql/queries/getCustomerComplaintCount';
import {
    CustomerComplaintHqListFilter,
    CustomerComplaintListFilter,
    CustomerComplaintTab,
    TAB_TO_QUERY_PARAMS,
    WorkbenchMode,
} from '../graphql/types/CustomerComplaintType';

type TabCountResponse = GetCustomerComplaintCountResponse
    & GetCustomerComplaintCountForWmsResponse
    & GetCustomerComplaintCountForHqResponse;

/** 三视角的 count 变量类型：HQ 的 filter 不含 warehouseId，故与 DSP/wms 不是同一类型 */
type TabCountVariables = GetCustomerComplaintCountVariables | {filter: CustomerComplaintHqListFilter};

/** baseFilter 按视角取不同形状：HQ 无 warehouseId */
type TabCountBaseFilter =
    | Omit<CustomerComplaintListFilter, 'statusList' | 'isFinished'>
    | Omit<CustomerComplaintHqListFilter, 'statusList' | 'isFinished'>;

/**
 * 三 Tab 计数 hook。
 *
 * 当前激活的 tab 直接复用列表查询的 totalElements（不发 count RPC），
 * 另外两个 tab 各发一次 count query。
 *
 * 按视角选择接口：
 *   - DSP：共用接口（旧角色鉴权）
 *   - wms：ForWms（资源码 + 仓库越权双重鉴权）
 *   - HQ：ForHq（纯资源码 CS_DSP_APPEAL_SUMMARY，全美无仓库）
 */
export function useCustomerComplaintTabCounts(
    activeTab: CustomerComplaintTab,
    baseFilter: TabCountBaseFilter | null,
    activeTabTotalElements: number,
    mode: WorkbenchMode = 'DSP',
) {
    const isHq = mode === 'HQ';
    const isWms = mode === 'wms';
    const countQuery = isHq
        ? GET_CUSTOMER_COMPLAINT_COUNT_FOR_HQ
        : isWms
            ? GET_CUSTOMER_COMPLAINT_COUNT_FOR_WMS
            : GET_CUSTOMER_COMPLAINT_COUNT;
    const tabs: CustomerComplaintTab[] = [
        CustomerComplaintTab.PENDING,
        CustomerComplaintTab.APPEALING,
        CustomerComplaintTab.CLOSED,
    ];

    // HQ 无 warehouseId，不需要 DSP/wms 那个 {warehouseId: 0} 的占位兜底
    const buildVars = (tab: CustomerComplaintTab): TabCountVariables => ({
        filter: {
            ...(baseFilter ?? (isHq ? {} : {warehouseId: 0})),
            ...TAB_TO_QUERY_PARAMS[tab],
        },
    } as TabCountVariables);

    const skipFor = (tab: CustomerComplaintTab) => tab === activeTab || baseFilter == null;

    const pendingQ = useQuery<TabCountResponse, TabCountVariables>(
        countQuery,
        {
            variables: buildVars(CustomerComplaintTab.PENDING),
            skip: skipFor(CustomerComplaintTab.PENDING),
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
        },
    );
    const appealingQ = useQuery<TabCountResponse, TabCountVariables>(
        countQuery,
        {
            variables: buildVars(CustomerComplaintTab.APPEALING),
            skip: skipFor(CustomerComplaintTab.APPEALING),
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
        },
    );
    const closedQ = useQuery<TabCountResponse, TabCountVariables>(
        countQuery,
        {
            variables: buildVars(CustomerComplaintTab.CLOSED),
            skip: skipFor(CustomerComplaintTab.CLOSED),
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
        },
    );

    const queryMap: Record<CustomerComplaintTab, typeof pendingQ> = {
        [CustomerComplaintTab.PENDING]: pendingQ,
        [CustomerComplaintTab.APPEALING]: appealingQ,
        [CustomerComplaintTab.CLOSED]: closedQ,
    };

    const getCount = (tab: CustomerComplaintTab): number | undefined => {
        if (tab === activeTab) return activeTabTotalElements;
        const q = queryMap[tab];
        if (q.loading) return undefined;
        if (isHq) return q.data?.getCustomerComplaintCountForHq ?? 0;
        if (isWms) return q.data?.getCustomerComplaintCountForWms ?? 0;
        return q.data?.getCustomerComplaintCountByFilters ?? 0;
    };

    const refetchAll = useCallback(() => {
        tabs.forEach((tab) => {
            if (skipFor(tab)) return;
            queryMap[tab].refetch();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, JSON.stringify(baseFilter)]);

    return {getCount, refetchAll};
}
